import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import PageHeader from "@/components/PageHeader";

// Consolidated page shell: a shared header + a sticky tab bar that switches
// between embedded page sections. Tabs are keyed and reflected in the ?tab= param.
export default function TabHub({ eyebrow, title, subtitle, tabs, testid }) {
  const [params, setParams] = useSearchParams();
  const paramTab = tabs.find((t) => t.key === params.get("tab"))?.key;
  const [active, setActive] = useState(paramTab || tabs[0].key);
  const contentRef = useRef(null);

  const scrollToSection = () => {
    requestAnimationFrame(() => {
      if (!contentRef.current) return;
      const y = contentRef.current.getBoundingClientRect().top + window.scrollY - 130;
      window.scrollTo({ top: Math.max(y, 0), behavior: "smooth" });
    });
  };

  // Sync active tab when the ?tab= param changes (e.g. via an in-page Link).
  useEffect(() => {
    if (paramTab && paramTab !== active) {
      setActive(paramTab);
      scrollToSection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramTab]);

  const select = (key) => {
    if (key === active) return;
    setActive(key);
    const next = new URLSearchParams(params);
    next.set("tab", key);
    setParams(next, { replace: true });
    scrollToSection();
  };

  const ActiveComponent = tabs.find((t) => t.key === active)?.component;

  return (
    <div data-testid={testid}>
      <PageHeader eyebrow={eyebrow} title={title} subtitle={subtitle} />

      <div className="sticky top-20 z-40 bg-black/90 backdrop-blur-xl border-y border-gold/15">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex gap-2 sm:gap-8 h-14 items-center overflow-x-auto">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => select(t.key)}
              data-testid={`tab-${t.key}`}
              className={`relative whitespace-nowrap font-mono text-[11px] tracking-[0.18em] uppercase py-2 transition-colors duration-300 ${
                active === t.key ? "text-gold" : "text-[#A19D98] hover:text-[#F5F0EB]"
              }`}
            >
              {t.label}
              {active === t.key && (
                <span className="absolute -bottom-[1px] left-0 right-0 h-[2px] bg-gold" />
              )}
            </button>
          ))}
        </div>
      </div>

      <div ref={contentRef}>{ActiveComponent}</div>
    </div>
  );
}
